import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { locationInfo } from '../mockData';

const Location = () => {
  // Using Google Maps Embed API
  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=64+Stewarts+Road,London+SW8+4DE,UK&zoom=15`;

  return (
    <div className="min-h-screen" data-testid="location-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-24">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=2070&auto=format&fit=crop"
            alt="London skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-4">
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: '"Cinzel", serif' }}
          >
            Location
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Visit us at our Stewarts Road facility in Nine Elms
          </p>
        </div>
      </section>

      {/* Location Details */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2
                className="text-4xl font-bold text-[#1F2D3D] mb-8"
                style={{ fontFamily: '"Cinzel", serif' }}
              >
                Get In Touch
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#1F2D3D]/10 rounded-full flex items-center justify-center mr-4">
                    <MapPin className="text-[#1F2D3D]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2D3D] mb-1" style={{ fontFamily: '"Cinzel", serif' }}>Address</h3>
                    <p className="text-gray-600">{locationInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#1F2D3D]/10 rounded-full flex items-center justify-center mr-4">
                    <Phone className="text-[#1F2D3D]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2D3D] mb-1" style={{ fontFamily: '"Cinzel", serif' }}>Phone</h3>
                    <a href={`tel:${locationInfo.phone}`} className="text-gray-600 hover:text-[#1F2D3D] transition-colors">
                      {locationInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#1F2D3D]/10 rounded-full flex items-center justify-center mr-4">
                    <Mail className="text-[#1F2D3D]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2D3D] mb-1" style={{ fontFamily: '"Cinzel", serif' }}>Email</h3>
                    <a href={`mailto:${locationInfo.email}`} className="text-gray-600 hover:text-[#1F2D3D] transition-colors">
                      {locationInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#1F2D3D]/10 rounded-full flex items-center justify-center mr-4">
                    <Clock className="text-[#1F2D3D]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2D3D] mb-2" style={{ fontFamily: '"Cinzel", serif' }}>Opening Hours</h3>
                    <div className="text-gray-600 space-y-1">
                      <p>{locationInfo.hours.weekdays}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div>
              <div className="bg-gray-200 rounded-lg overflow-hidden shadow-lg h-[500px]">
                <iframe
                  title="Tuxedo Express Location"
                  src={mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Directions */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1F2D3D] mb-8 text-center"
            style={{ fontFamily: '"Cinzel", serif' }}
          >
            How to Find Us
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <h3 className="font-semibold text-[#1F2D3D] mb-2" style={{ fontFamily: '"Cinzel", serif' }}>By Tube</h3>
              <p className="text-gray-600">
                Vauxhall (5 min walk)<br />
                Nine Elms (8 min walk)
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <h3 className="font-semibold text-[#1F2D3D] mb-2" style={{ fontFamily: '"Cinzel", serif' }}>By Bus</h3>
              <p className="text-gray-600">
                Routes: 77, 87, 156, 344<br />
                Stop: Stewarts Road
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <h3 className="font-semibold text-[#1F2D3D] mb-2" style={{ fontFamily: '"Cinzel", serif' }}>Parking</h3>
              <p className="text-gray-600">
                On-site parking available<br />
                Free customer parking
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;
